import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { AgentInstallInput, AgentResultInput, AgentResultParams } from './types';

const toastErr = (err: ApiError) => {
  if (!err.fieldErrors) toast.error(err.message);
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const agentTemplateKeys = {
  all: ['agent-templates'] as const,
  list: () => ['agent-templates', 'list'] as const,
  detail: (id: string | number) => ['agent-templates', 'detail', id] as const,
};

export const agentInstallKeys = {
  all: ['agent-installs'] as const,
  list: (p: Record<string, unknown>) => ['agent-installs', 'list', p] as const,
};

export const agentResultKeys = {
  all: ['agent-results'] as const,
  list: (p: AgentResultParams) => ['agent-results', 'list', p] as const,
  detail: (id: string | number) => ['agent-results', 'detail', id] as const,
};

// ---------------------------------------------------------------------------
// Templates (global catalog, read-only)
// ---------------------------------------------------------------------------
export function useAgentTemplates(enabled = true) {
  return useQuery({
    queryKey: agentTemplateKeys.list(),
    queryFn: () => api.templatesApi.list(),
    enabled,
  });
}

/**
 * Template detail incl. each version's `prompt_md` (the operating manual).
 * Kept out of the list query — fetch only when a detail view is open, so
 * `id` is nullable and the query stays disabled until then.
 */
export function useAgentTemplate(id: number | null) {
  return useQuery({
    queryKey: agentTemplateKeys.detail(id ?? ''),
    queryFn: () => api.templatesApi.get(id as number),
    enabled: id != null,
  });
}

/** Upload (file) or clear (file=null) an agent's platform icon. Invalidates the
 *  catalog + installs so every surface picks up the new image. */
export function useSetAgentImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string | number; file: File | null }) =>
      file ? api.templatesApi.uploadImage(id, file) : api.templatesApi.clearImage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentTemplateKeys.all });
      qc.invalidateQueries({ queryKey: agentInstallKeys.all });
      toast.success('Agent image updated');
    },
    onError: toastErr,
  });
}

// ---------------------------------------------------------------------------
// Installs (list / create / update / remove)
// ---------------------------------------------------------------------------
export function useAgentInstalls(params: Record<string, unknown> = {}, enabled = true) {
  return useQuery({
    queryKey: agentInstallKeys.list(params),
    queryFn: () => api.installsApi.list(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCreateAgentInstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AgentInstallInput) => api.installsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentInstallKeys.all });
      toast.success('Agent installed');
    },
    onError: toastErr,
  });
}

export function useUpdateAgentInstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | number; input: AgentInstallInput }) =>
      api.installsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentInstallKeys.all });
      toast.success('Agent updated');
    },
    onError: toastErr,
  });
}

export function useDeleteAgentInstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.installsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentInstallKeys.all });
      toast.success('Agent removed');
    },
    onError: toastErr,
  });
}

// ---------------------------------------------------------------------------
// Results (list / detail)
// ---------------------------------------------------------------------------
export function useAgentResults(params: AgentResultParams = {}) {
  return useQuery({
    queryKey: agentResultKeys.list(params),
    queryFn: () => api.resultsApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useAgentResult(id: string | number | null) {
  return useQuery({
    queryKey: agentResultKeys.detail(id ?? ''),
    queryFn: () => api.resultsApi.get(id as string | number),
    enabled: id != null,
  });
}

export function useUpdateAgentResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | number; input: AgentResultInput }) =>
      api.resultsApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: agentResultKeys.all }),
    onError: toastErr,
  });
}
